// Fleet approvals - shared by the fleet card, the emails (api/_approvals.js) and the morning briefing (api/_agent.js).
// WHO may approve is decided only in the database (sql/fleet-approvals.sql: hc_my_approvals, po_next_slot); this file only formats.
import { fmtMoney } from './poCore.js'

export const NEEDS_LABEL = { full: 'HOD / chief', captain: 'Captain', management: 'Management', captain_or_management: 'Captain or management', close: 'Close the APA' }
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function waitingHours(since, now = Date.now()) {
  const t = Date.parse(since)
  return Number.isFinite(t) ? Math.max(0, (now - t) / 3600000) : 0
}
// "under 1 h", "5 h", "1 day", "3 days"
export function waitingLabel(since, now = Date.now()) {
  const h = waitingHours(since, now)
  if (h < 1) return 'under 1 h'
  if (h < 24) return Math.floor(h) + ' h'
  const d = Math.floor(h / 24)
  return d + (d === 1 ? ' day' : ' days')
}
export const amountText = r => (r.amount == null || r.amount === '' ? (r.kind === 'po' ? 'no price yet' : '') : fmtMoney(Number(r.amount), r.currency)) + (r.kind === 'apa' && r.amount != null ? ' balance' : '')
export const itemTitle = r => r.kind === 'apa' ? 'APA ' + (r.ref || r.number || '') + ' - close requested' : (r.ref || r.number || 'Purchase order')

// fleet-2: the card header - what the waiting ORDERS are worth, per currency, biggest first ("21,370 EUR waiting").
// An APA close request carries the APA balance, which is not spend, so it is not added.
export function waitingTotals(rows) {
  const by = {}
  let noPrice = 0
  for (const r of rows || []) {
    if (r.kind !== 'po') continue
    if (r.amount == null || r.amount === '' || !Number.isFinite(Number(r.amount))) { noPrice++; continue }
    const c = String(r.currency || '').toUpperCase() || '?'
    by[c] = (by[c] || 0) + Number(r.amount)
  }
  return { totals: Object.entries(by).map(([currency, amount]) => ({ currency, amount })).sort((a, b) => b.amount - a.amount), noPrice }
}
export function waitingTotalText(rows) {
  const { totals, noPrice } = waitingTotals(rows)
  if (!totals.length && !noPrice) return ''
  const money = totals.map(t => Math.round(t.amount).toLocaleString('en-GB') + (t.currency === '?' ? '' : ' ' + t.currency)).join(' + ')
  return (money ? money + ' waiting' : '') + (noPrice ? (money ? ' · ' : '') + noPrice + ' without a price' : '')
}
// Approve / Reject on the card (sql/fleet-2.sql hc_approval_decide): the confirm, the reason rule, the result line
export const REASON_MIN = 3
export const reasonOk = s => String(s || '').trim().length >= REASON_MIN
export function confirmText(r, decision) {
  const amt = r.amount == null || r.amount === '' ? '' : fmtMoney(Number(r.amount), r.currency)
  if (r.kind === 'apa') return decision === 'approve'
    ? 'Close the APA ' + (r.ref || '') + ' on ' + (r.vessel_name || 'this vessel') + (amt ? ' (balance ' + amt + ')' : '') + '? The crew asked to close it; closing locks the APA.'
    : 'Not yet: the APA ' + (r.ref || '') + ' on ' + (r.vessel_name || 'this vessel') + ' stays open. ' + (r.requested_by || 'The requester') + ' sees your reason.'
  return decision === 'approve'
    ? 'Approve ' + (r.ref || 'this order') + (amt ? ' · ' + amt : ' (no price yet)') + ' for ' + (r.vessel_name || 'this vessel') + '? Nothing is sent to the supplier until someone presses Send.'
    : 'Reject ' + (r.ref || 'this order') + (amt ? ' · ' + amt : '') + ' on ' + (r.vessel_name || 'this vessel') + '? It goes back to ' + (r.requested_by || 'the requester') + ' as a draft, with your reason.'
}
export function decisionText(res, r) {
  const ref = (res && res.ref) || r.ref || ''
  if (r.kind === 'apa') return res && res.status === 'closed' ? 'APA closed: ' + ref + ' - the requester is told' : 'Close request answered: ' + ref + ' - the requester is told'
  if (res && res.status === 'rejected') return 'Rejected: ' + ref + ' - the requester and the Captain are told'
  if (res && res.status === 'approved') return 'Approved: ' + ref
  if (res && res.count != null) return ref + ': ' + res.count + ' of ' + res.required + ' approved - waiting for the other approval'
  return 'Done: ' + ref
}
// the database has no hc_approval_decide yet (sql/fleet-2.sql not run)
export const decideMissing = msg => /hc_approval_decide|does not exist|schema cache|Could not find the function/i.test(String(msg || ''))

// morning briefing line for Captain / management: "3 orders waiting for your approval (1 over 2 days)"; null when nothing waits
export function briefingLine(rows, now = Date.now()) {
  const pos = (rows || []).filter(r => r.kind === 'po'), apas = (rows || []).filter(r => r.kind === 'apa')
  if (!pos.length && !apas.length) return null
  const old = pos.filter(r => waitingHours(r.waiting_since, now) > 48).length
  const parts = []
  if (pos.length) parts.push(pos.length + ' order' + (pos.length > 1 ? 's' : '') + ' waiting for your approval (' + old + ' over 2 days)')
  if (apas.length) parts.push(apas.length + ' APA close request' + (apas.length > 1 ? 's' : ''))
  return parts.join('; ')
}

// link in the email -> the app opens that PO / APA (App.jsx reads it once after login)
export function deepLink(app, { kind, open_id, client_id }) {
  return String(app || '').replace(/\/$/, '') + '/?open=' + (kind === 'apa' ? 'apa' : 'po') + '&id=' + encodeURIComponent(open_id || '') + (client_id ? '&v=' + encodeURIComponent(client_id) : '')
}
// a related document in a risk assessment PDF (src/lib/raStandard.js appUrl): ?open=go&page=<one of GO_PAGES>&q=<text>&id=<uuid>
export const GO_PAGES = ['checklists', 'equipment', 'crew_certs', 'risk_assessments', 'manuals', 'drill_matrix', 'lsa']
export function parseDeepLink(search) {
  const q = new URLSearchParams(search || '')
  const kind = q.get('open'), id = q.get('id'), v = q.get('v')
  if (kind === 'go') {
    const page = q.get('page')
    if (!GO_PAGES.includes(page) || (id && !UUID.test(id))) return null
    return { kind, id: id || null, v: null, page, q: String(q.get('q') || '').slice(0, 120) }
  }
  if (kind === 'sop') return UUID.test(id || '') && (!v || UUID.test(v)) ? { kind, id, v: v || null, mode: q.get('mode') === 'read' ? 'read' : 'do' } : null   // sop-visual: the QR label opens the SOP in Do it mode
  if (!['po', 'apa'].includes(kind) || !UUID.test(id || '') || (v && !UUID.test(v))) return null
  return { kind, id, v: v || null }
}
// what the app stores so the PO / APA page opens the right record (both pages already read these keys);
// 'go' (a risk assessment's related document): the page opens filtered to q (hc_open_q), a checklist by its title
export const openKeys = (kind, id, title, link) => kind === 'go' && link ? (link.page === 'checklists' && link.q ? { page: 'checklists', key: 'hc_open_checklist', value: link.q }
    : { page: link.page, key: 'hc_open_q', value: JSON.stringify({ page: link.page, q: link.q || '', id: link.id || null }) })
  : kind === 'sop' ? { page: 'sops', key: 'hc_open_sop', value: id, extra: { hc_open_sop_mode: (link && link.mode) || 'do' } }
  : kind === 'apa' ? { page: 'apa', key: 'hc_apa_charter', value: id }
  : kind === 'document' ? { page: 'docs', key: 'hc_open_doc', value: title || '' }          // Documents / Crew open a record by its title
  : kind === 'crew_doc' ? { page: 'crew', key: 'hc_open_crew_title', value: title || '' }
  : kind === 'wishlist' ? { page: 'purchase_orders', key: 'hc_po_tab', value: 'wishlist' }      // low stock (sql/test-fixes-1.sql)
  : kind === 'survey' ? { page: 'survey_readiness', key: 'hc_survey_open', value: id || '' }     // survey due in 60 / 30 days (sql/feat-10b.sql)
  : { page: 'purchase_orders', key: 'hc_open_po', value: id }

// one email per notice: { subject, title, subtitle, intro, rows: [[label, value]], button, link }
const NOTICE_INTRO = {
  po_waiting: 'This purchase order is waiting for your approval.',
  po_approved: 'This purchase order is approved.',
  po_rejected: 'This purchase order was rejected. It stays a draft: change it and it goes back for approval.',
  apa_close_waiting: 'The crew asks you to close this APA.',
  apa_closed: 'The APA you asked to close is closed.',
  apa_close_rejected: 'Your request to close this APA was not approved.',
  doc_expired: 'This ship document has expired (it was valid through its expiry date). Renew it and upload the new one in Documents - the renewal task on the To Do list closes by itself.',
  stock_low: 'An item dropped to or below its minimum stock and is on the wishlist. Nothing is ordered or sent automatically (a critical spare goes on a draft order of its preferred supplier).',
  cert_expired: 'This crew certificate has expired (it was valid through its expiry date). Renew it and upload the new one on the Crew page - the renewal task on the To Do list closes by itself.',
  survey_due: 'A survey is coming up. Survey Readiness shows what is missing and makes the To Dos in one click.',
}
export function noticeEmail(n, app) {
  if (n.ref_type === 'document' || n.ref_type === 'crew_doc') {     // expiry notices (sql/consistency.sql hc_expiry_run)
    const d = n.data || {}, crew = n.ref_type === 'crew_doc'
    return {
      subject: (d.vessel ? d.vessel + ' · ' : '') + n.title, title: n.title, subtitle: (d.vessel || '') + (d.number ? ' · ' + d.number : ''),
      intro: NOTICE_INTRO[n.kind] || '', rows: [['Vessel', d.vessel || '-'], [crew ? 'Certificate' : 'Document', d.title || '-'], ['Number', d.number || '-'], ['Expiry date', d.expiry || '-']],
      button: 'Open Hey Captain', link: String(app || '').replace(/\/$/, '') + '/',
    }
  }
  if (n.ref_type === 'survey') {        // next survey due in 60 / 30 days (sql/feat-10b.sql hc_survey_reminders)
    const d = n.data || {}
    return { subject: (d.vessel ? d.vessel + ' · ' : '') + n.title, title: n.title, subtitle: d.vessel || '', intro: NOTICE_INTRO.survey_due,
      rows: [['Vessel', d.vessel || '-'], ['Survey', d.survey || '-'], ['Due', d.due || '-'], ['Days left', d.days == null ? '-' : String(d.days)]],
      button: 'Open Survey Readiness', link: String(app || '').replace(/\/$/, '') + '/' }
  }
  if (n.ref_type === 'wishlist') {      // low stock (sql/test-fixes-1.sql hc_stock_wishlist)
    const d = n.data || {}
    const rows = [['Vessel', d.vessel || '-'], ['Item', d.item || '-'], ['Left / minimum', (d.quantity ?? '-') + ' / ' + (d.min ?? '-')]]
    if (d.department) rows.push(['Department', d.department])
    rows.push(['Order', d.po_number ? 'Draft ' + d.po_number + ' (not sent)' : 'On the wishlist'])
    return { subject: (d.vessel ? d.vessel + ' · ' : '') + n.title, title: n.title, subtitle: d.vessel || '', intro: NOTICE_INTRO.stock_low, rows,
      button: 'Open the wishlist', link: String(app || '').replace(/\/$/, '') + '/' }
  }
  const d = n.data || {}, kind = n.ref_type === 'apa' ? 'apa' : 'po'
  const amount = d.amount == null ? (kind === 'po' ? 'no price yet' : '-') : fmtMoney(Number(d.amount), d.currency) + (kind === 'apa' ? ' (balance)' : '')
  const rows = [['Vessel', d.vessel || '-'], [kind === 'apa' ? 'Charter' : 'PO number', d.number || '-'], ['Amount', amount], ['Requested by', d.requested_by || '-']]
  if (kind === 'po' && d.supplier) rows.push(['Supplier', d.supplier])
  if (n.body) rows.push([n.kind === 'po_waiting' || n.kind === 'apa_close_waiting' ? 'Note' : 'Details', n.body])
  return {
    subject: (d.vessel ? d.vessel + ' · ' : '') + n.title,
    title: n.title, subtitle: (d.vessel || '') + (d.number ? ' · ' + d.number : ''),
    intro: NOTICE_INTRO[n.kind] || '', rows,
    button: kind === 'apa' ? 'Open the APA' : 'Open the purchase order',
    link: deepLink(app, { kind, open_id: n.open_id || n.ref_id, client_id: n.client_id }),
  }
}

// the daily reminder: one email per login with every item waiting more than 24 h
export function digestEmail(items, app, now = Date.now()) {
  const list = (items || []).map(i => {
    const kind = i.kind === 'apa' ? 'apa' : 'po'
    return {
      title: (i.vessel ? i.vessel + ' · ' : '') + (kind === 'apa' ? 'APA ' + (i.number || '') + ' - close' : (i.number || 'Purchase order')),
      sub: [i.amount == null ? (kind === 'po' ? 'no price yet' : '') : fmtMoney(Number(i.amount), i.currency) + (kind === 'apa' ? ' balance' : ''), i.requested_by ? 'requested by ' + i.requested_by : ''].filter(Boolean).join(' · '),
      pill: 'waiting ' + waitingLabel(i.since, now), old: waitingHours(i.since, now) > 48,
      link: deepLink(app, { kind, open_id: i.open_id || i.id, client_id: i.client_id }),
    }
  })
  const n = list.length
  return { subject: n + ' item' + (n === 1 ? '' : 's') + ' waiting for your approval', items: list }
}
